import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ContainerComponent } from '@abook-ui/components/container/container.component';
import { isExternalUrl } from '@abook-ui/utils/is-external-url';
import { toE164 } from '@abook-ui/utils/to-e164';
import { SiteStore } from '@core/site/site.store';
import type { FooterLegalLink, TenantFooterConfig } from '@core/tenant/tenant-config.model';

const LEGAL_LINKS: readonly { key: FooterLegalLink; label: string }[] = [
  { key: 'privacy-policy', label: 'Privatlivspolitik' },
  { key: 'terms-and-conditions', label: 'Handelsbetingelser' },
  { key: 'cookie-policy', label: 'Cookiepolitik' },
  { key: 'accessibility', label: 'Tilgængelighed' },
];

type SocialKey = keyof NonNullable<TenantFooterConfig['social']>;

/**
 * Brand marks as 18×18 SVG paths, in display order. A Record, so a network added to the model
 * without an icon here is a compile error.
 */
const SOCIAL_ICONS: Record<SocialKey, { label: string; path: string }> = {
  facebook: {
    label: 'Facebook',
    path: 'M17 9.05A8 8 0 1 0 9.58 17v-5.61H7.54V9.05h2.04V7.28c0-2.02 1.2-3.13 3.04-3.13.88 0 1.8.16 1.8.16v1.98H13.4c-1 0-1.3.62-1.3 1.26v1.5h2.22l-.36 2.34H12.1V17A8 8 0 0 0 17 9.05Z',
  },
  instagram: {
    label: 'Instagram',
    path: 'M9 1.62c2.4 0 2.69.01 3.63.05 2.38.11 3.49 1.24 3.6 3.6.04.94.05 1.22.05 3.63 0 2.4-.01 2.69-.05 3.63-.11 2.36-1.21 3.49-3.6 3.6-.94.04-1.22.05-3.63.05-2.4 0-2.69-.01-3.63-.05-2.38-.11-3.49-1.24-3.6-3.6C1.63 11.59 1.62 11.3 1.62 9c0-2.4.01-2.69.05-3.63.11-2.36 1.22-3.49 3.6-3.6C6.31 1.63 6.6 1.62 9 1.62ZM9 0C6.56 0 6.25.01 5.3.05 2.14.2.2 2.14.05 5.3.01 6.25 0 6.56 0 9s.01 2.75.05 3.7c.15 3.16 2.09 5.1 5.25 5.25C6.25 17.99 6.56 18 9 18s2.75-.01 3.7-.05c3.15-.15 5.1-2.09 5.25-5.25.04-.95.05-1.26.05-3.7s-.01-2.75-.05-3.7C17.8 2.15 15.86.2 12.7.05 11.75.01 11.44 0 9 0Zm0 4.38a4.62 4.62 0 1 0 0 9.24 4.62 4.62 0 0 0 0-9.24ZM9 12a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm4.8-8.84a1.08 1.08 0 1 0 0 2.16 1.08 1.08 0 0 0 0-2.16Z',
  },
  linkedin: {
    label: 'LinkedIn',
    path: 'M16.33 0H1.67C.75 0 0 .75 0 1.67v14.66C0 17.25.75 18 1.67 18h14.66c.92 0 1.67-.75 1.67-1.67V1.67C18 .75 17.25 0 16.33 0ZM5.52 15.33H2.76V6.75h2.76v8.58ZM4.14 5.54a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2Zm11.19 9.79h-2.76v-4.17c0-1-.02-2.28-1.39-2.28-1.39 0-1.6 1.08-1.6 2.2v4.25H6.82V6.75h2.65v1.17h.04c.37-.7 1.27-1.44 2.6-1.44 2.79 0 3.3 1.83 3.3 4.22v4.63Z',
  },
};
const SOCIAL_LINKS = (Object.keys(SOCIAL_ICONS) as SocialKey[]).map((key) => ({
  key,
  ...SOCIAL_ICONS[key],
}));

@Component({
  selector: 'app-footer',
  imports: [RouterLink, MatIconModule, ContainerComponent],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppFooterComponent {
  protected site = inject(SiteStore);
  protected currentYear = new Date().getFullYear();
  protected footerConfig = computed(() => this.site.tenantConfig().shared.footer);

  /** An external URL renders as `href`; anything else (the default or an app path) as a route. */
  protected legalLinks = computed(() => {
    const overrides = this.footerConfig().legalLinks ?? {};
    return LEGAL_LINKS.flatMap(({ key, label }) => {
      const override = overrides[key];
      if (override === false) return [];
      const link = override ?? `/legal/${key}`;
      return [isExternalUrl(link) ? { key, label, href: link } : { key, label, route: link }];
    });
  });

  protected socialLinks = computed(() => {
    const social = this.footerConfig().social ?? {};
    return SOCIAL_LINKS.flatMap(({ key, label, path }) => {
      const url = social[key];
      return url ? [{ key, label, path, url }] : [];
    });
  });

  protected contact = computed(() => this.footerConfig().contact ?? {});
  protected phoneHref = computed(() => {
    const phone = this.contact().phone;
    return phone ? `tel:${toE164(phone)}` : undefined;
  });

  protected hasSocial = computed(() => this.socialLinks().length > 0 || !!this.contact().email);
  protected hasContact = computed(() => Object.values(this.contact()).some(Boolean));
  protected showThirdColumn = computed(
    () => this.footerConfig().showThirdColumn !== false && (this.hasSocial() || this.hasContact()),
  );
}
